import { useNavigate } from 'react-router-dom';
import { AGENT_AVATARS_COUNT } from '../config/agentConfig';
import { defaultUserDataPrompt } from '../constants/agentConstants';
import { ChatSize } from '../enums/ChatAgentCreationStep';
import {
  Agent,
  AgentFormData,
  AgentRole,
  AgentStatus,
  DepartmentContact,
} from '../interfaces/Agent';
import { WorkflowType } from '../interfaces/App';
import { CalendarStateData } from '../interfaces/Calendar';
import { UserDataPromptField } from '../interfaces/ChatAgent';
import { TranslationKey } from '../types/i18n';
import { isLocalHost } from './environmentHelper';
import { detectEmbedMode } from './embedMode';

const agentRoleToLabel = (role: string) => {
  switch (role) {
    case 'receptionist':
      return 'Receptionist';
    default:
      return 'AI Agent';
  }
};

const agentStatusToLabel = (
  status: string,
  t: (key: TranslationKey) => string
) => {
  switch (status) {
    case 'online':
      return t('Online');
    case 'offline':
      return t('Offline');
    default:
      return t('In Review');
  }
};

const getAgentStatusColor = (statusColor: AgentStatus) => {
  switch (statusColor) {
    case AgentStatus.ONLINE:
      return 'var(--online-color)';
    case AgentStatus.OFFLINE:
      return 'var(--offline-color)';
    default:
      return 'var(--in-review-color)';
  }
};

const getShortenValue = (value: number) => {
  return new Intl.NumberFormat('lb-LU', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2,
  }).format(value);
};

const hasCustomPhoneNumber = (agent: Agent) => {
  return (
    agent.custom_phone_number &&
    agent.custom_phone_number !== agent.system_phone_number
  );
};

const getAgentPstnNumber = (agent: Agent): string =>
  (agent.custom_phone_number || agent.system_phone_number || '').trim();

const getAgentSipAddress = (agent: Agent): string =>
  (agent.sip_address || '').trim();

// Builds the `external_capabilities.transfer_calls` payload for both the
// employee_directory mode and the manual department-contact list mode.
const buildTransferCallsCapability = (
  transferCalls: DepartmentContact[] | 'employee_directory'
): { transfer_calls?: DepartmentContact[] | 'employee_directory' } => {
  if (transferCalls === 'employee_directory') {
    return { transfer_calls: 'employee_directory' };
  }

  if (!Array.isArray(transferCalls) || transferCalls.length === 0) {
    return {};
  }

  return {
    transfer_calls: transferCalls.map(item => ({
      department_name: item.department_name,
      phone_number: removeWhiteSpaces(item.phone_number),
      ...(item.email ? { email: item.email } : {}),
    })),
  };
};

const removeWhiteSpaces = (data: string): string => {
  return data?.split(' ')?.join('');
};

const setAgentsImageFile = () => {
  const id = Math.floor(Math.random() * AGENT_AVATARS_COUNT) + 1;
  return `agent${id}.webp`;
};

const snakeCaseToCapitalized = (str: string) => {
  if (typeof str !== 'string' || str.length === 0) {
    return ''; // Return an empty string for invalid input
  }

  // Split the kebab-cased string into an array of words
  const words = str.split('_');

  // Capitalize each word in the array
  const capitalizedWords = words.map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  // Join the array of capitalized words with a space
  const capitalizedString = capitalizedWords.join(' ');

  return capitalizedString;
};

const usedCustomPhoneNumbers = (agents: Agent[] | null | undefined) => {
  if (!agents || !Array.isArray(agents)) {
    return [];
  }
  return agents
    .filter(agent => agent.custom_phone_number != '')
    .map(agent => agent.custom_phone_number);
};

const usedSystemPhoneNumbers = (agents: Agent[] | null | undefined) => {
  if (!agents || !Array.isArray(agents)) {
    return [];
  }
  return agents
    .filter(agent => agent.system_phone_number != '')
    .map(agent => agent.system_phone_number);
};

const openCalendarOAuthUrl = (authUrl: string) => {
  if (detectEmbedMode()) {
    sessionStorage.setItem('calendar_oauth_popup', '1');
    window.open(
      authUrl,
      'elora_calendar_oauth',
      'width=520,height=720,noopener,noreferrer'
    );
    return;
  }

  window.location.href = authUrl;
};

const handleConnectToCalendar = (
  agentId?: string,
  calendarProvider?: string,
  workflow?: WorkflowType
) => {
  const redirectUri = isLocalHost()
    ? 'http://localhost:3000/auth/callback'
    : 'https://app.elora.lu/auth/callback';
  const encodedRedirectUri = encodeURIComponent(redirectUri);

  const expirationDays = workflow === WorkflowType.CREATE ? 1 : 90;

  // Create state parameter with additional data
  const stateData: CalendarStateData = {
    id: agentId,
    app: calendarProvider,
    workflow,
    expirationDays,
  };
  const encodedState = encodeURIComponent(JSON.stringify(stateData));

  // Set flag to disable beforeunload during OAuth redirect
  sessionStorage.setItem('calendar_oauth_in_progress', 'true');

  // Handle Google Calendar OAuth
  if (calendarProvider === 'google') {
    const googleClientId =
      '1020915595360-dgcl3e0ass0htrab4cest86ed0ddupao.apps.googleusercontent.com';
    if (!googleClientId) {
      console.error('Google OAuth Client ID is not configured');
      return;
    }

    const googleOAuthUrl = new URL(
      'https://accounts.google.com/o/oauth2/v2/auth'
    );
    googleOAuthUrl.searchParams.append('client_id', googleClientId);
    googleOAuthUrl.searchParams.append('redirect_uri', redirectUri);
    googleOAuthUrl.searchParams.append('response_type', 'code');
    googleOAuthUrl.searchParams.append(
      'scope',
      'https://www.googleapis.com/auth/calendar'
    );
    googleOAuthUrl.searchParams.append('access_type', 'offline');
    googleOAuthUrl.searchParams.append('prompt', 'consent');
    googleOAuthUrl.searchParams.append('state', encodedState);

    openCalendarOAuthUrl(googleOAuthUrl.toString());
  } else {
    // Handle Microsoft Outlook OAuth (default)
    const outlookClientId = 'cfc0f791-0367-4e16-aeb8-374524690a64';
    const scopes = encodeURIComponent(
      'offline_access Calendars.Read Calendars.ReadWrite'
    );

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${outlookClientId}&response_type=code&redirect_uri=${encodedRedirectUri}&response_mode=query&scope=${scopes}&state=${encodedState}&prompt=select_account`;
    openCalendarOAuthUrl(authUrl);
  }
};

const newCallAgentRedirect = (navigate: ReturnType<typeof useNavigate>) => {
  navigate('/agents/call/new?step=2');
};

const editCallAgentRedirect = (
  navigate: ReturnType<typeof useNavigate>,
  agentId?: string
) => {
  navigate(`/agents/call/edit/${agentId}?step=2`);
};

const redirectToCallAgentsPage = (
  navigate: ReturnType<typeof useNavigate>,
  parsedStateData: any
) => {
  if (parsedStateData?.workflow === WorkflowType.CREATE) {
    newCallAgentRedirect(navigate);
  } else if (parsedStateData?.id) {
    editCallAgentRedirect(navigate, parsedStateData?.id);
  } else {
    newCallAgentRedirect(navigate);
  }
};

// AgentFormData['transfer_calls'] is either an array of department contacts
// or the string 'employee_directory' — this narrows to the array case,
// defaulting to empty, for call sites that only care about contact rows.
const getTransferCallsArray = (
  transferCalls: AgentFormData['transfer_calls']
): DepartmentContact[] => (Array.isArray(transferCalls) ? transferCalls : []);

const getDefaultAgentRoleId = (agentRoles: AgentRole[]): string | undefined => {
  return agentRoles.find(
    agentRole => agentRole?.name.toLowerCase() === 'receptionist'
  )?.id;
};

const transformUserDataPrompt = (userDataPrompts: string[]) => {
  return defaultUserDataPrompt.filter(item =>
    userDataPrompts.includes(item.id)
  );
};

const reverseTransformUserDataPrompt = (
  userDataPrompts: UserDataPromptField[]
): string[] => {
  return userDataPrompts?.map(item => item.id);
};

const getDimension = (chatSize: ChatSize) => {
  switch (chatSize) {
    case ChatSize.MEDIUM:
      return {
        width: '768px',
        height: '698px',
      };
    case ChatSize.LARGE:
      return {
        width: '1095px',
        height: '904px',
      };
    default:
      return {
        width: '400px',
        height: '683px',
      };
  }
};

export {
  agentRoleToLabel,
  agentStatusToLabel,
  buildTransferCallsCapability,
  editCallAgentRedirect,
  getDimension,
  getDefaultAgentRoleId,
  getAgentPstnNumber,
  getAgentSipAddress,
  getAgentStatusColor,
  getShortenValue,
  getTransferCallsArray,
  handleConnectToCalendar,
  hasCustomPhoneNumber,
  newCallAgentRedirect,
  redirectToCallAgentsPage,
  removeWhiteSpaces,
  reverseTransformUserDataPrompt,
  setAgentsImageFile,
  snakeCaseToCapitalized,
  transformUserDataPrompt,
  usedCustomPhoneNumbers,
  usedSystemPhoneNumbers,
};
